import { useMemo } from 'react';

import { useTabsState } from '@cast/design-system';
import { useDebounce } from '@cast/utils';

import { useDatePresetValue } from 'components/search';
import {
  useWorkloadAutoscalerEventsQuery,
  UseWorkloadEventsQueryArgs,
} from 'hooks/queries/workload-optimization';
import { useParams } from 'hooks/useParams';

import { prepareEvent } from './_utils';
import { clampEventsToDateToNow } from '../../_utils';
import { EventLogTabs } from '../../event-log/types';

import { UseServerData } from '@cast/shared-search-ui';

export const useWorkloadAutoscalerEvents: UseServerData = ({
  values,
  enabled,
}) => {
  const { clusterId } = useParams<any>();
  const context = useTabsState<EventLogTabs>();
  const currentTab = context?.currentTab;
  const { dateRange, types, workloadId, workloadName, kind, namespace } =
    useDebounce(values);
  const { fromDate, toDate: rawToDate } = useDatePresetValue(dateRange, true);
  const toDate = clampEventsToDateToNow(rawToDate);
  const queryKey = Object.values(dateRange).join('-');

  const queryFilter: UseWorkloadEventsQueryArgs = {
    clusterId,
    types,
    workloadId,
    workloadName,
    namespace,
    kind,
    fromDate,
    toDate,
    queryKey,
    pageLimit: currentTab === EventLogTabs.TIMELINE ? undefined : '100',
    enabled,
  };

  const res = useWorkloadAutoscalerEventsQuery(queryFilter);

  const events = useMemo(() => {
    return res.events?.map(prepareEvent);
  }, [res.events]);

  return {
    data: events,
    isLoading: res.isLoading,
    hasNextPage: !res.isLoading && res.hasNextPage,
    fetchNextPage: res.fetchNextPage,
    hasPreviousPage: !res.isLoading,
    hasFiltersApplied: true, // Since there is always a date filter applied
    error: res.error,
    refetch: res.refetch,
    isFetching: res.isFetching,
  };
};
